import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  afterNextRender,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';

/**
 * Mobile-nav primitive (DESIGN.md §9, Wave A responsive foundation).
 *
 * At ≤ 900 px (--tb-bp-md): renders as a hamburger button (48 px target) + slide-out drawer.
 * At > 900 px: the drawer content is always visible (it becomes the regular sidebar).
 *
 * Usage (in each app-shell):
 *   <tb-mobile-nav [label]="'CashBro navigation'" (navReady)="navOpen=$event">
 *     <!-- nav items, identical to desktop rail -->
 *     <ng-content />
 *   </tb-mobile-nav>
 *
 * A11y:
 *   - Focus trapped inside the drawer when open (CDK FocusTrap would be ideal;
 *     here we use a lightweight manual trap via focusable-elements query so there is
 *     no CDK dependency in this primitive — bring in CDK FocusTrap for the Console build).
 *   - ESC closes + returns focus to the trigger.
 *   - Swipe-left (touch) closes the drawer.
 *   - WCAG 2.2 AA: trigger is 48×48 px minimum; focus ring via brand-accent.
 */
@Component({
  selector: 'tb-mobile-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Hamburger trigger — visible only at mobile/tablet (≤ md breakpoint) -->
    <button
      #trigger
      class="hamburger"
      [attr.aria-expanded]="open()"
      aria-haspopup="dialog"
      [attr.aria-label]="open() ? 'Close navigation' : 'Open navigation'"
      (click)="toggle()"
      data-testid="mobile-nav-trigger"
    >
      <span class="bar" [class.bar--open]="open()"></span>
      <span class="bar" [class.bar--open]="open()"></span>
      <span class="bar" [class.bar--open]="open()"></span>
    </button>

    <!-- Drawer backdrop — closes on click; only rendered when open -->
    @if (open()) {
      <div
        class="backdrop"
        aria-hidden="true"
        (click)="close()"
        data-testid="mobile-nav-backdrop"
      ></div>
    }

    <!-- Drawer (always in DOM; visibility/transform driven by open()) -->
    <div
      #drawer
      class="drawer"
      [class.drawer--open]="open()"
      role="dialog"
      [attr.aria-modal]="open() ? 'true' : null"
      [attr.aria-label]="label()"
      data-testid="mobile-nav-drawer"
    >
      <div class="drawer-inner">
        <ng-content />
      </div>
    </div>
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      flex: 0 0 auto;
    }

    /* ── Hamburger trigger ─────────────────────────────────────────── */
    .hamburger {
      display: none; /* hidden at desktop */
      flex-direction: column; justify-content: center; align-items: center; gap: 5px;
      width: var(--tb-touch, 48px); height: var(--tb-touch, 48px);
      padding: 0;
      background: none; border: none; cursor: pointer;
      border-radius: var(--tb-radius-sm, 8px);
      color: var(--tb-text-muted, #9aa5b1);
      flex-shrink: 0;
      z-index: 6100;
      position: relative;
    }
    .hamburger:hover { background: var(--tb-surface-inset, #11141b); color: var(--tb-text-primary, #e6e9ef); }
    .hamburger:focus-visible { outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 2px; }

    .bar {
      display: block; width: 22px; height: 2px;
      background: currentColor; border-radius: 2px;
      transition: transform 200ms ease, opacity 200ms ease;
      transform-origin: center;
    }
    /* X state when open */
    .bar:nth-child(1).bar--open { transform: translateY(7px) rotate(45deg); }
    .bar:nth-child(2).bar--open { opacity: 0; }
    .bar:nth-child(3).bar--open { transform: translateY(-7px) rotate(-45deg); }

    /* ── Backdrop ───────────────────────────────────────────────────── */
    .backdrop {
      position: fixed; inset: 0; z-index: 6000;
      background: rgba(0, 0, 0, 0.55);
      animation: tb-fade-in 200ms ease;
    }
    @keyframes tb-fade-in { from { opacity: 0; } to { opacity: 1; } }

    /* ── Drawer ─────────────────────────────────────────────────────── */
    .drawer {
      /* At desktop: fixed rail below the sticky app bar; app shells reserve matching space. */
      position: fixed;
      top: var(--tb-app-bar-height, var(--tb-touch, 48px));
      left: 0;
      bottom: 0;
      z-index: 90;
      width: var(--tb-nav-rail-width, 212px);
      box-sizing: border-box;
      padding: 1rem 0.6rem;
      border-right: 1px solid var(--tb-border-line, #2a2f3a);
      background: var(--tb-surface, #161a23);
    }
    .drawer-inner { height: 100%; overflow-y: auto; }

    /* ── Mobile / tablet (≤ 900 px) ────────────────────────────────── */
    @media (max-width: 900px) {
      .hamburger { display: flex; }

      .drawer {
        /* Slide-in panel */
        position: fixed; top: 0; left: 0; bottom: 0; z-index: 6050;
        width: 280px; max-width: 85vw;
        border-right: 1px solid var(--tb-border-line, #2a2f3a);
        box-shadow: 4px 0 24px rgba(0,0,0,.5);
        transform: translateX(-100%);
        transition: transform 200ms ease;
        /* flex-basis override at mobile */
        flex: none;
        padding: 1.5rem 0.75rem;
        /* safe-area for iPhone notch */
        padding-top: max(1.5rem, env(safe-area-inset-top));
        padding-left: max(0.75rem, env(safe-area-inset-left));
      }
      .drawer--open { transform: translateX(0); }
    }

    @media (prefers-reduced-motion: reduce) {
      .bar { transition: none; }
      .backdrop { animation: none; }
      .drawer { transition: none; }
    }
  `,
})
export class TbMobileNavComponent implements OnDestroy {
  /** Accessible name for the drawer dialog. */
  readonly label = input('Primary navigation');

  /** Emits true when opened, false when closed. */
  readonly navToggled = output<boolean>();

  readonly open = signal(false);

  private readonly doc = inject(DOCUMENT);
  private readonly el = inject(ElementRef);
  private drawerEl = viewChild<ElementRef>('drawer');
  private triggerEl = viewChild<ElementRef>('trigger');

  // Touch-swipe tracking
  private touchStartX = 0;

  constructor() {
    afterNextRender(() => {
      this.drawerEl()?.nativeElement?.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.drawerEl()?.nativeElement?.addEventListener('touchend', this.onTouchEnd, { passive: true });
    });
  }

  ngOnDestroy(): void {
    this.drawerEl()?.nativeElement?.removeEventListener('touchstart', this.onTouchStart);
    this.drawerEl()?.nativeElement?.removeEventListener('touchend', this.onTouchEnd);
    this.unlockScroll();
  }

  toggle(): void {
    if (this.open()) { this.close(); } else { this.open_(); }
  }

  open_(): void {
    this.open.set(true);
    this.navToggled.emit(true);
    this.lockScroll();
    // Move focus into the drawer on next frame
    setTimeout(() => this.focusFirstInDrawer(), 50);
  }

  close(): void {
    this.open.set(false);
    this.navToggled.emit(false);
    this.unlockScroll();
    // Restore focus to the trigger
    (this.triggerEl()?.nativeElement as HTMLElement)?.focus();
  }

  @HostListener('keydown.escape')
  onEsc(): void {
    if (this.open()) this.close();
  }

  /** Trap Tab within the drawer when open. */
  @HostListener('keydown.tab', ['$event'])
  @HostListener('keydown.shift.tab', ['$event'])
  onTab(e: Event): void {
    const ke = e as KeyboardEvent;
    if (!this.open()) return;
    const drawer = this.drawerEl()?.nativeElement as HTMLElement;
    if (!drawer) return;
    const focusable = Array.from(
      drawer.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (ke.shiftKey && this.doc.activeElement === first) {
      ke.preventDefault();
      last.focus();
    } else if (!ke.shiftKey && this.doc.activeElement === last) {
      ke.preventDefault();
      first.focus();
    }
  }

  private focusFirstInDrawer(): void {
    const drawer = this.drawerEl()?.nativeElement as HTMLElement;
    const first = drawer?.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    first?.focus();
  }

  private lockScroll(): void {
    this.doc.body.style.overflow = 'hidden';
  }
  private unlockScroll(): void {
    this.doc.body.style.overflow = '';
  }

  private readonly onTouchStart = (e: TouchEvent) => {
    this.touchStartX = e.touches[0]?.clientX ?? 0;
  };

  private readonly onTouchEnd = (e: TouchEvent) => {
    const dx = (e.changedTouches[0]?.clientX ?? 0) - this.touchStartX;
    // Swipe left > 60 px closes the drawer
    if (dx < -60 && this.open()) this.close();
  };
}
